import { useState, useContext } from "react";
import ExperienceContext from "./ExperienceContext";
import {
	Group,
	Button,
	Image,
	Tooltip,
	Divider,
	useMantineTheme,
} from "@mantine/core";
import HostspotControls from "./HotspotControls";
import { RAD2DEG } from "three/src/math/MathUtils";
import { showSuccessNotification } from "../../utils/Notifications";
import imageIcon from "../../static/svg/image.svg";
import videoIcon from "../../static/svg/video.svg";
import panoramaIcon from "../../static/svg/panorama.svg";
import hotspotIcon from "../../static/svg/hotspot.svg";
import infospotIcon from "../../static/svg/infospot.svg";
import videoPlaneIcon from "../../static/svg/video-plane.svg";
import box3DIcon from "../../static/svg/3d-box.svg";
import linkIcon from "../../static/svg/link.svg";
import audioIcon from "../../static/svg/audio.svg";
import cameraTargetIcon from "../../static/images/camera-target.png";
import TransformControls from "./TransformControls";
import InfospotControls from "./InfospotControls";
import InfospotModal from "./InfospotModal";
import VideoPlaneModal from "./VideoPlaneModal";
import VideoPlaneControls from "./VideoPlaneControls";
import InfoImageSpotControls from "./InfoImageSpotControls";
import InfoImageSpotModal from "./InfoImageSpotModal";
import AddPanoSpotModal from "./PanoSpotModal";
import PanoSpotControls from "./PanoSpotControls";
import VideoSpotModal from "./VideoSpotModal";
import VideoSpotControls from "./VideoSpotControls";
import ThreeDSpotModal from "./ThreeDSpotModal";
import ThreeDSpotControls from "./ThreeDSpotControls";
import EmbedLinkModal from "./EmbedLinkModal";
import EmbedLinkControls from "./EmbedLinkControls";
import AudioSpotModal from "./AudioSpotModal";
import AudioSpotControls from "./AudioSpotControls";
function ControlsPanel({
	activePanoId,
	selectedObject,
	transformControl,
	orbitControl,
	setSelectedObject,
	closeControls,
}) {
	const theme = useMantineTheme();
	const { addNewHotspot, setCameraTarget } = useContext(ExperienceContext);
	const [isInfoSpotOpen, setIsInfoSpotOpen] = useState(false);
	const [isVideoPlaneOpen, setIsVideoPlaneOpen] = useState(false);
	const [isInfoImageSpotOpen, setIsInfoImageSpotOpen] = useState(false);
	const [isPanoSpotOpen, setIsPanoSpotOpen] = useState(false);
	const [isVideoSpotOpen, setIsVideoSpotOpen] = useState(false);
	const [isThreeDSpotOpen, setIsThreeDSpotOpen] = useState(false);
	const [isEmbedLinkSpotOpen, setIsEmbedLinkSpotOpen] = useState(false);
	const [isAudioSpotOpen, setIsAudioSpotOpen] = useState(false);
	const SetPanoCameraTarget = () => {
		setCameraTarget(activePanoId, [
			orbitControl.azimuthAngle * RAD2DEG,
			orbitControl.polarAngle * RAD2DEG,
		]);
		showSuccessNotification("Camera Target Set");
	};
	return (
		<Group
			style={{
				position: "absolute",
				width: "100%",
				bottom: "20px",
				left: "50%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				transform: "translateX(-50%)",
			}}
		>
			<Group
				spacing={5}
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				{selectedObject?.type === "hotspot" ? (
					<HostspotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "text" ? (
					<InfospotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "link" ? (
					<EmbedLinkControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "image" ? (
					<InfoImageSpotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "video" ? (
					<VideoSpotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "inline-video" ? (
					<VideoPlaneControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "pano" ? (
					<PanoSpotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "three-d" ? (
					<ThreeDSpotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				{selectedObject?.type === "audio" ? (
					<AudioSpotControls
						activePanoId={activePanoId}
						selectedObject={selectedObject}
						closeControls={closeControls}
					/>
				) : null}
				<TransformControls
					selectedObject={selectedObject}
					transformControl={transformControl}
					setSelectedObject={setSelectedObject}
				/>
			</Group>
			<Group spacing={5} style={{ zIndex: "102" }}>
				<Tooltip label="Add Image" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsInfoImageSpotOpen(true);
						}}
					>
						<Image src={imageIcon} width={25} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Video" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsVideoSpotOpen(true);
						}}
					>
						<Image src={videoIcon} width={25} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Panorama" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsPanoSpotOpen(true);
						}}
					>
						<Image src={panoramaIcon} width={27} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Hotspot" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0px 0px 0px 0px",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							addNewHotspot(activePanoId);
							closeControls();
						}}
					>
						<Image src={hotspotIcon} width={25} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Information" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsInfoSpotOpen(true);
						}}
					>
						<Image src={infospotIcon} width={12} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Video Plane" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsVideoPlaneOpen(true);
						}}
					>
						<Image src={videoPlaneIcon} width={25} />
					</Button>
				</Tooltip>
				<Tooltip label="Add 3D Model" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsThreeDSpotOpen(true);
						}}
					>
						<Image src={box3DIcon} width={25} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Link" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsEmbedLinkSpotOpen(true);
						}}
					>
						<Image src={linkIcon} width={25} />
					</Button>
				</Tooltip>
				<Tooltip label="Add Audio" withArrow>
					<Button
						variant="light"
						size="lg"
						radius="md"
						style={{
							width: "50px",
							padding: "0",
						}}
						sx={(theme) => ({
							backgroundColor: "#09254eb3",
							"&:hover": {
								backgroundColor: theme.colors.ivisit_dark[2],
							},
						})}
						onClick={() => {
							setIsAudioSpotOpen(true);
						}}
					>
						<Image src={audioIcon} width={25} />
					</Button>
				</Tooltip>
				{activePanoId && (
					<>
						<Divider
							orientation="vertical"
							size="md"
							color={theme.colors.ivisit_dark[2]}
						/>
						<Tooltip label="Set Initial Camera Target" withArrow>
							<Button
								variant="light"
								size="lg"
								radius="md"
								style={{
									width: "50px",
									padding: "0",
								}}
								sx={(theme) => ({
									backgroundColor: "#09254eb3",
									"&:hover": {
										backgroundColor:
											theme.colors.ivisit_dark[2],
									},
								})}
								onClick={SetPanoCameraTarget}
							>
								<Image src={cameraTargetIcon} width={25} />
							</Button>
						</Tooltip>
					</>
				)}
			</Group>
			<InfospotModal
				isInfoSpotOpen={isInfoSpotOpen}
				setIsInfoSpotOpen={setIsInfoSpotOpen}
				activePanoId={activePanoId}
			/>
			<VideoPlaneModal
				isVideoPlaneOpen={isVideoPlaneOpen}
				setIsVideoPlaneOpen={setIsVideoPlaneOpen}
				activePanoId={activePanoId}
			/>
			<InfoImageSpotModal
				isInfoImageSpotOpen={isInfoImageSpotOpen}
				setIsInfoImageSpotOpen={setIsInfoImageSpotOpen}
				activePanoId={activePanoId}
			/>
			<AddPanoSpotModal
				isPanoSpotOpen={isPanoSpotOpen}
				setIsPanoSpotOpen={setIsPanoSpotOpen}
				activePanoId={activePanoId}
			/>
			<VideoSpotModal
				isVideoSpotOpen={isVideoSpotOpen}
				setIsVideoSpotOpen={setIsVideoSpotOpen}
				activePanoId={activePanoId}
			/>
			<ThreeDSpotModal
				isThreeDSpotOpen={isThreeDSpotOpen}
				setIsThreeDSpotOpen={setIsThreeDSpotOpen}
				activePanoId={activePanoId}
			/>
			<EmbedLinkModal
				isEmbedLinkSpotOpen={isEmbedLinkSpotOpen}
				setIsEmbedLinkSpotOpen={setIsEmbedLinkSpotOpen}
				activePanoId={activePanoId}
			/>
			<AudioSpotModal
				isAudioSpotOpen={isAudioSpotOpen}
				setIsAudioSpotOpen={setIsAudioSpotOpen}
				activePanoId={activePanoId}
			/>
		</Group>
	);
}

export default ControlsPanel;
